import * as React from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Navbar } from '@/components/navbar'
import { Button } from '@/components/ui/button'
import { ArrowLeft, ShieldCheck, DollarSign, Clock, RefreshCw } from 'lucide-react'
import { motion } from 'framer-motion'

export function PurchasePolicyPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const ref = searchParams.get('ref')
  
  const handleBackToArticle = () => {
    if (ref) {
      try {
        const slug = atob(ref)
        navigate(`/posts/${slug}`)
      } catch {
        navigate('/')
      }
    } else {
      navigate('/')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back button */}
        <button
          onClick={handleBackToArticle}
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-medium">{ref ? 'Back to Article' : 'Back to Home'}</span>
        </button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Article Purchase Policy
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Everything you need to know about purchasing articles on Newsne.ws
          </p>
        </motion.div>

        {/* Key Points */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-2 gap-4 mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <DollarSign className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">One-Time Payment</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Pay $0.29 once and read the article anytime, forever.
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <Clock className="w-8 h-8 text-green-600 dark:text-green-400 mb-3" />
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Instant Access</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Get immediate access to the full article after purchase.
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <ShieldCheck className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-3" />
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Secure Payment</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              All transactions are encrypted and processed securely.
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <RefreshCw className="w-8 h-8 text-orange-600 dark:text-orange-400 mb-3" />
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">No Expiration</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Purchased articles never expire. Read them anytime.
            </p>
          </div>
        </motion.div>

        {/* Policy Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 space-y-6"
        >
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              How Article Purchases Work
            </h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              Some premium articles on Newsne.ws require a one-time purchase to access the full content. 
              This allows us to support quality journalism while giving you the flexibility to pay only 
              for the articles you want to read.
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
              <li>Each article costs $0.29 (unless otherwise specified)</li>
              <li>Payment is processed instantly through our secure payment system</li>
              <li>Once purchased, the article is added to your account</li>
              <li>You can access purchased articles anytime from your dashboard</li>
              <li>No subscription required - pay only for what you read</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Payment & Billing
            </h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              We accept all major credit cards and process payments securely through Stripe.
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
              <li>Payments are processed in USD</li>
              <li>You'll receive an email receipt after each purchase</li>
              <li>View all your purchases in your dashboard</li>
              <li>Prices are clearly displayed before purchase</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Refund Policy
            </h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              We want you to be satisfied with your purchases. Here's our refund policy:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
              <li>Article purchases are generally non-refundable</li>
              <li>If you experience technical issues preventing access, contact support</li>
              <li>Refunds may be issued at our discretion for valid technical issues</li>
              <li>Duplicate purchases will be refunded automatically</li>
              <li>Contact support within 24 hours for refund requests</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Alternative Options
            </h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              If you don't want to purchase an article, you have other options:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
              <li><strong>Read on Original Site:</strong> Click the link to read the article on the publisher's website</li>
              <li><strong>Subscribe:</strong> Get unlimited access to all articles with a premium subscription</li>
              <li><strong>Free Articles:</strong> Browse our selection of free articles</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Account & Access
            </h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              Your purchased articles are tied to your account:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
              <li>You must be logged in to purchase articles</li>
              <li>Purchased articles are accessible from any device</li>
              <li>Articles remain in your account even if you cancel your subscription</li>
              <li>You can download or print purchased articles for personal use</li>
              <li>Sharing purchased articles is not permitted</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Questions?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              If you have questions about article purchases or need assistance, please contact our support team:
            </p>
            <div className="flex gap-4">
              <Link to="/faq">
                <Button variant="outline">
                  View FAQs
                </Button>
              </Link>
              <Button>
                Contact Support
              </Button>
            </div>
          </section>
        </motion.div>

        {/* Back to Article CTA */}
        {ref && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-center"
          >
            <Button onClick={handleBackToArticle} size="lg">
              Back to Article
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  )
}
